import React, { Suspense, useEffect, useRef, useState } from 'react';
import { ButtonBase, CircularProgress, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';

import { BodyModelViewer, Box, Disclaimer, YearSlider } from './DigitalTwinShared';
import { DiseaseRiskDetail } from './digitalTwinApi';

export type DiseaseInfo = {
  /** AP_009_002 의 질환 키 — 화면 5로 어떤 질환인지 넘길 때 쓴다 */
  key?: string;
  label: string;
  icon?: string;
  status?: '나쁨' | '주의' | '양호';
  /** AP_009_002 의 질환 판단 원문 */
  detail?: DiseaseRiskDetail;
  /** 서버 등급 문구를 3단 배지로 환산하지 못한 경우 — 원문을 강조해 보여준다 */
  gradeUnrecognized?: boolean;
};

type DigitalTwinScreen4Props = {
  open: boolean;
  onClose: () => void;
  disease?: DiseaseInfo | null;
};

const STATUS_COLOR: Record<string, string> = {
  '나쁨': '#ed5454',
  '주의': '#ff8000',
  '양호': '#38c172',
};

/**
 * 시트 안 3D 모델 영역의 높이(px).
 *
 * 본문이 스크롤되므로 시트 높이에 눌리지 않는다 — 이 값을 키우면 모델이 그만큼
 * 크게 보이고, 넘치는 만큼 본문이 아래로 스크롤된다.
 *
 * 모델을 키울 때 카메라 zoom 대신 이 값을 쓰는 이유: zoom 은 화각 안으로 들어오는
 * 범위를 좁히는 것이라 1.07 을 넘기면 머리·발이 잘린다. 캔버스를 키우면 같은
 * 세로 범위를 더 많은 픽셀로 그리므로 전신을 유지한 채 커진다.
 */
// 시트가 화면 높이의 90%(ratio)만 차지하므로, 모델 높이를 고정하면 세로가 짧은
// 기기(아이폰 SE 등)에서 모델이 시트를 꽉 채워 답답하다. 뷰포트 높이에 비례시키되
// 아래로 잘라(clamp) 준다 — 큰 화면(14 Pro Max)에서는 520px 를 유지한다.
const MODEL_HEIGHT_MAX = 520;       // 최대(기준) 높이 — 큰 화면 기준
const MODEL_HEIGHT_MIN = 320;       // 최소 높이 — 아주 작은 화면에서도 이보다 작아지지 않음
const MODEL_VH_RATIO = 0.62;        // 14 Pro Max 에서 520px 가 나오는 비율

const computeModelHeight = (): number => {
  if (typeof window === 'undefined') return MODEL_HEIGHT_MAX;
  const vh = window.innerHeight || 900;
  return Math.round(Math.max(MODEL_HEIGHT_MIN, Math.min(MODEL_HEIGHT_MAX, vh * MODEL_VH_RATIO)));
};

/** 뷰포트 높이에 맞춘 시트 안 3D 모델 높이(px). 회전·리사이즈 시 갱신된다. */
const useModelHeight = (): number => {
  const [h, setH] = useState<number>(computeModelHeight);
  useEffect(() => {
    const onResize = () => setH(computeModelHeight());
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, []);
  return h;
};

/**
 * 상태 배지(양호/주의/나쁨) 아래와 모델 머리 끝 사이의 실제 간격(px).
 *
 * 눈에 보이는 간격은 CSS 여백만으로 정해지지 않는다 — 캔버스는 카메라에 들어오는
 * 세로 범위(약 2.91)에 모델(높이 2.7)을 가운데 두므로, 캔버스 위쪽에 늘 빈 띠가 생긴다.
 * 그래서 CSS 여백에서 그 빈 띠만큼을 빼야 원하는 간격이 나온다.
 */
const BADGE_TO_MODEL_GAP = 64;

/**
 * 캔버스 위쪽에 생기는 빈 띠의 비율 (캔버스 높이 대비).
 *
 * 카메라 화각만으로 계산하면 3.6% 가 나오지만 실제로는 7.3% 다 — 모델의 바운딩
 * 박스가 보이는 머리끝보다 위로 더 뻗어 있어서, 박스를 가운데 맞추면 머리가
 * 그만큼 아래로 내려간다. 그래서 계산값 대신 실측값을 쓴다.
 * (520px 캔버스에서 배지 밑~머리끝 38px 로 측정)
 *
 * 빈 띠는 캔버스 높이에 비례하므로, 모델 높이가 반응형으로 줄어도 같은 비율로
 * 계산해 위쪽 여백(MODEL_MARGIN_TOP)을 그때그때 구한다 — 컴포넌트 안 참고.
 */
const MODEL_TOP_GAP_RATIO = 0.073;

const DEFAULT_RATIO = 0.9; // 처음 열릴 때 화면의 90%
const MIN_RATIO = 0.25;    // 이보다 작아지면 닫힘
const MAX_RATIO = 0.9;     // 최대 90%까지 확장
const CLOSE_VELOCITY = 0.7; // 빠르게 내리면 닫힘 (px/ms)

/**
 * 화면 4: 질환별 변화 분석 BottomSheet (드래그 가능)
 *
 * 동작:
 *  - 열면 화면 하단에서 슬라이드 업 (기본 50% 높이)
 *  - 상단 핸들 또는 시트 영역을 터치/마우스로 위·아래 드래그하면 높이 조절
 *  - MIN_RATIO 아래로 내리거나 빠르게 아래로 스와이프하면 닫힘
 *  - 외부 백드롭(반투명 검정) 클릭으로도 닫힘
 */
// 연도 대신 시점 라벨 — 0:3개월전, 1:현재, 2:5년 후, 3:10년 후
const YEAR_STOPS = [0, 1, 2, 3];
const YEAR_LABELS = ['3개월전', '현재', '5년 후', '10년 후'];
const CURRENT_YEAR = 1; // '현재'

const DigitalTwinScreen4: React.FC<DigitalTwinScreen4Props> = ({ open, onClose, disease }) => {
  const navigate = useNavigate();
  const [year, setYear] = useState<number>(CURRENT_YEAR);

  // 3D 모델 높이 — 화면 크기에 따라 반응형. 위쪽 여백도 높이에 맞춰 함께 계산한다.
  const modelHeight = useModelHeight();
  const modelInnerPad = Math.round(modelHeight * MODEL_TOP_GAP_RATIO);
  const modelMarginTop = BADGE_TO_MODEL_GAP - modelInnerPad;
  // 회전 제스처를 받을 영역(모델 몸통 위) — 이 영역에서만 회전, 밖에서는 시트 스크롤
  const [rotateZone, setRotateZone] = useState<HTMLDivElement | null>(null);

  // sheet 높이 비율 (0~1)
  const [ratio, setRatio] = useState<number>(DEFAULT_RATIO);
  const [animating, setAnimating] = useState(true);

  // 비만 클릭 시에만 시점 드래그에 따라 비만 morph 적용
  // 현재(1) → 0, 5년 후(2) → 0.5, 10년 후(3) → 1.0 (3개월전은 0)
  const obesityLevel = disease?.label === '비만'
    ? Math.max(0, Math.min(1, (year - CURRENT_YEAR) / 2))
    : 0;

  // 드래그 상태
  const dragRef = useRef<{
    startY: number;
    startRatio: number;
    startTime: number;
    lastY: number;
    lastTime: number;
    pointerId: number | null;
  } | null>(null);

  // open 변경 시 ratio + year 리셋
  useEffect(() => {
    if (open) {
      setAnimating(true);
      setRatio(DEFAULT_RATIO);
      setYear(CURRENT_YEAR);
    }
  }, [open]);

  const handleDetail = () => {
    onClose();
    // 화면 3(예측 위험도)으로 보낸다 — 장기 상세와 같은 화면을 질환 모드로 연다.
    //  화면 3 은 ?disease= 로 어떤 질환인지 받아 등급·신뢰도·근거·부족 데이터·해석과
    //  질병 설명을 보여준다. 시점(year)은 넘기지 않는다 — 화면 3 에는 시점 개념이 없다.
    const q = new URLSearchParams();
    if (disease?.key) q.set('disease', disease.key);
    navigate(`/digitalTwin/screen3?${q.toString()}`);
  };

  const label = disease?.label ?? '비만';
  const status = disease?.status;
  const detail = disease?.detail;

  // === 드래그 핸들러 (Pointer Events: 터치·마우스 통합) ===
  // 상단 핸들바에만 붙인다. 본문에 붙이면 세로 스크롤 제스처와 구분할 수 없어
  // 스크롤하려는 동작이 시트 높이 조절·닫기로 오인된다.
  const onPointerDown = (e: React.PointerEvent) => {
    setAnimating(false);
    const y = e.clientY;
    const now = Date.now();
    dragRef.current = {
      startY: y,
      startRatio: ratio,
      startTime: now,
      lastY: y,
      lastTime: now,
      pointerId: e.pointerId,
    };
    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (!dragRef.current || dragRef.current.pointerId !== e.pointerId) return;
    const dy = e.clientY - dragRef.current.startY;
    const vh = window.innerHeight;
    const next = dragRef.current.startRatio - dy / vh;
    setRatio(Math.max(0.05, Math.min(MAX_RATIO, next)));
    dragRef.current.lastY = e.clientY;
    dragRef.current.lastTime = Date.now();
  };

  const onPointerUp = (e: React.PointerEvent) => {
    if (!dragRef.current || dragRef.current.pointerId !== e.pointerId) return;
    const { lastY, lastTime, startY, startTime, startRatio } = dragRef.current;
    const totalDy = lastY - startY;
    const totalDt = lastTime - startTime || 1;
    const velocity = totalDy / totalDt; // px/ms (+면 아래로)
    dragRef.current = null;
    setAnimating(true);

    // 닫기 판정 (많이 내리거나 빠르게 아래로 스와이프)
    if (ratio < MIN_RATIO || velocity > CLOSE_VELOCITY) {
      onClose();
      return;
    }
    // 2단계 스냅: 시작 위치보다 위로 올렸으면 최대(MAX), 아니면 기본(50%)
    //  - 50%에서 위로 드래그 → 자동으로 최대까지
    //  - 최대에서 아래로 드래그 → 50%로 (50%~최대 중간 정착 없음)
    if (ratio > startRatio + 0.01) setRatio(MAX_RATIO);
    else setRatio(DEFAULT_RATIO);
  };

  if (!open) return null;

  return (
    <>
      {/* 백드롭 */}
      <Box
        onClick={onClose}
        sx={{
          position: 'fixed',
          inset: 0,
          bgcolor: 'rgba(0,0,0,.35)',
          zIndex: 1200,
          animation: 'fadeIn .2s ease',
          '@keyframes fadeIn': {
            from: { opacity: 0 },
            to: { opacity: 1 },
          },
        }}
      />

      {/* BottomSheet */}
      <Box
        sx={{
          position: 'fixed',
          left: '50%',
          bottom: 0,
          transform: 'translateX(-50%)',
          width: '100%',
          maxWidth: 430,
          height: `${ratio * 100}vh`,
          zIndex: 1300,
          bgcolor: '#eef2f6',
          borderRadius: '22px 22px 0 0',
          boxShadow: '0 -8px 24px rgba(80, 98, 107, .22)',
          p: '12px 18px 22px',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          transition: animating ? 'height .26s cubic-bezier(.32,.72,0,1)' : 'none',
          overflow: 'hidden',
        }}
      >
        {/* 드래그 핸들바 — 시트 높이 조절/닫기는 여기서만 받는다 */}
        <Box
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          sx={{
            py: 0.8,
            display: 'flex',
            justifyContent: 'center',
            cursor: 'grab',
            flexShrink: 0,
            touchAction: 'none',
            '&:active': { cursor: 'grabbing' },
          }}
        >
          <Box sx={{ width: 48, height: 4, bgcolor: '#c5d3da', borderRadius: '99px' }} />
        </Box>

        {/* 스크롤 본문 — 시트 높이에 눌려 찌그러지지 않도록 여기서 넘치면 스크롤한다.
            '자세히 분석' 버튼과 안내 문구는 이 영역 밖에 두어 항상 하단에 고정된다. */}
        <Box
          sx={{
            flex: 1,
            minHeight: 0,          // flex 자식이 내용 높이만큼 부풀어 스크롤이 안 생기는 것을 막는다
            overflowY: 'auto',
            overscrollBehavior: 'contain', // 끝까지 스크롤해도 뒤 페이지가 따라 움직이지 않게
            WebkitOverflowScrolling: 'touch',
            // 자식들이 기존과 동일하게 배치되도록 시트와 같은 flex 열을 유지한다.
            // 블록으로 두면 mx:'auto' 로 가운데 정렬하던 상태 뱃지가 가로 전체로 늘어난다.
            display: 'flex',
            flexDirection: 'column',
          }}
        >
        {/* 타이틀 */}
        <Typography
          sx={{
            textAlign: 'center',
            color: '#2A4268',
            mt: 1,
            mb: 1.8,
            flexShrink: 0,
            lineHeight: 1.2,
          }}
        >
          {/* 서버가 정식 명칭을 주면 그것을 쓴다 ('이상지질' → '이상지질혈증') */}
          <Box component="span" sx={{ fontSize: 25, fontWeight: 800 }}>
            {detail?.diseaseName ?? label}
          </Box>
          <Box component="span" sx={{ fontSize: 19, fontWeight: 400 }}>{' '}변화 분석</Box>
        </Typography>

        {/* 상태 뱃지 */}
        {status && (
          <Box
            sx={{
              mx: 'auto',
              // 배지~모델 간격은 모델 박스의 marginTop 에서 한 번에 잡는다
              mb: 0,
              px: 1.6,
              py: 0.3,
              borderRadius: '99px',
              bgcolor: STATUS_COLOR[status],
              color: '#fff',
              fontSize: 12,
              fontWeight: 800,
              flexShrink: 0,
            }}
          >
            {status}
          </Box>
        )}

        {/* 3D 모델 — 화면 1과 동일한 채도/밝기 필터 적용, 표정은 neutral
            스크롤 영역 안이라 flex 로 남는 공간을 먹는 방식이 통하지 않는다.
            높이를 직접 지정한다 — 이 값을 키우면 모델이 커지고 그만큼 아래가 스크롤된다. */}
        <Box
          sx={{
            height: modelHeight,
            flexShrink: 0,
            position: 'relative',
            mt: `${modelMarginTop}px`,
            mb: 1.2,
            filter: 'saturate(0.9) brightness(1.04)',
          }}
        >
          <Suspense
            fallback={
              <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress size={28} sx={{ color: '#8aa2ad' }} />
              </Box>
            }
          >
            {/* zoom 으로 키우지 않는다 — 카메라(fov 31, z 5.25)에 들어오는 세로 범위가
                약 2.91 이고 모델 높이가 2.7 이라, 배율이 1.07 을 넘으면 머리·발이 잘린다.
                크게 보이게 하려면 zoom 대신 MODEL_HEIGHT 를 키울 것 (캔버스가 커지면
                같은 세로 범위를 더 많은 픽셀로 그리므로 잘리지 않고 커진다). */}
            <BodyModelViewer
              expression="neutral"
              obesityLevel={obesityLevel}
              rotateEl={rotateZone}
            />
          </Suspense>
          {/* 회전 영역 — 모델 몸통 위에만 얹는다.
              이 영역 안: 좌우 드래그로 회전 / 이 영역 밖: 터치가 시트 본문으로 흘러 스크롤. */}
          <Box
            ref={setRotateZone}
            sx={{
              position: 'absolute',
              // 모델 몸통(중앙) 영역 — 좌우/상하에 스크롤용 여백을 남긴다
              left: '30%',
              right: '30%',
              top: '5%',
              bottom: '6%',
              zIndex: 1,
              cursor: 'grab',
              '&:active': { cursor: 'grabbing' },
            }}
          />
        </Box>

        {/* 연도 슬라이더 */}
        <Box sx={{ px: 1, mb: 3.5, flexShrink: 0 }}>
          <YearSlider years={YEAR_STOPS} labels={YEAR_LABELS} value={year} onChange={setYear} />
        </Box>

        {/* 서버 판단 근거 — 스펙이 결측 데이터를 숨기지 않는 설계를 명시하므로 그대로 노출한다.
            슬라이더 아래에 두어, 위쪽 공간은 3D 모델이 쓰도록 한다. */}
        {detail && (() => {
          // 섹션 라벨·강조선 색 — 상태(양호/주의/나쁨)를 따라간다. 상태가 없으면 기본 파랑.
          const accent = status ? STATUS_COLOR[status] : '#6e8df0';
          return (
          <Box
            sx={{
              flexShrink: 0,
              mb: 1.2,
              p: '16px 16px 18px',
              bgcolor: '#fff',
              borderRadius: '16px',
              boxShadow: '0 2px 10px rgba(80,98,107,.07)',
              // 시트 본문이 스크롤되므로 여기서 잘라낼 이유가 없다 — 전체를 그대로 보여준다.
            }}
          >
            {/* 위험 등급 — 왼쪽 상태색 강조선과 함께 헤더로 */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.1 }}>
              <Box
                sx={{
                  width: 4,
                  alignSelf: 'stretch',
                  minHeight: 22,
                  borderRadius: '99px',
                  bgcolor: accent,
                  flexShrink: 0,
                  mt: '2px',
                }}
              />
              <Typography sx={{ fontSize: 16, fontWeight: 800, color: '#2A4268', lineHeight: 1.4 }}>
                {detail.riskGrade}
              </Typography>
            </Box>

            {/* 지표 3칸 — 신뢰도 / 데이터 / 점수 */}
            <Box sx={{ mt: 1.6, display: 'flex', gap: 0.8 }}>
              {[
                { label: '신뢰도', value: detail.confidenceLevel },
                { label: '데이터', value: `${detail.dataCoverageRate}%` },
                { label: '점수', value: `${detail.score}` },
              ].map((s) => (
                <Box
                  key={s.label}
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    bgcolor: '#f3f6fa',
                    borderRadius: '12px',
                    py: 1.2,
                    px: 0.8,
                    textAlign: 'center',
                  }}
                >
                  <Typography sx={{ fontSize: 12.5, color: '#8c9aab', fontWeight: 600, lineHeight: 1.2 }}>
                    {s.label}
                  </Typography>
                  <Typography
                    sx={{
                      mt: 0.5,
                      fontSize: 16,
                      fontWeight: 800,
                      color: '#2A4268',
                      lineHeight: 1.2,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {s.value}
                  </Typography>
                </Box>
              ))}
            </Box>

            {/* 해석 */}
            {detail.interpretation && (
              <Box sx={{ mt: 2 }}>
                <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: accent, mb: 0.7 }}>
                  해석
                </Typography>
                <Typography sx={{ fontSize: 16, color: '#3d4859', lineHeight: 1.55 }}>
                  {detail.interpretation}
                </Typography>
              </Box>
            )}

            {/* 결측 데이터 — 칩 */}
            {(detail.missingData?.length ?? 0) > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: '#c2724a', mb: 0.7 }}>
                  {`결측 데이터 ${detail.missingData.length}`}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.7 }}>
                  {detail.missingData.map((m) => (
                    <Box
                      key={m}
                      sx={{
                        fontSize: 14,
                        color: '#c2724a',
                        bgcolor: '#fbefe8',
                        borderRadius: '8px',
                        px: 1.1,
                        py: 0.5,
                        lineHeight: 1.3,
                      }}
                    >
                      {m}
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {/* 근거 데이터 — 칩 */}
            {(detail.evidenceData?.length ?? 0) > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: '#5a6b80', mb: 0.7 }}>
                  {`근거 데이터 ${detail.evidenceData.length}`}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.7 }}>
                  {detail.evidenceData.map((m) => (
                    <Box
                      key={m}
                      sx={{
                        fontSize: 14,
                        color: '#5a6b80',
                        bgcolor: '#eef2f6',
                        borderRadius: '8px',
                        px: 1.1,
                        py: 0.5,
                        lineHeight: 1.3,
                      }}
                    >
                      {m}
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {/* 자동 분류 주의 문구 */}
            {disease?.gradeUnrecognized && (
              <Box sx={{ mt: 2, p: '10px 12px', bgcolor: '#fdf6e8', borderRadius: '10px' }}>
                <Typography sx={{ fontSize: 13, color: '#b07f2a', lineHeight: 1.45 }}>
                  위 배지는 서버 등급 문구를 자동 분류한 값입니다. 판단은 원문을 기준으로 확인해 주세요.
                </Typography>
              </Box>
            )}
          </Box>
          );
        })()}

        {/* 자세히 분석 버튼 — 본문과 함께 스크롤된다 */}
        <ButtonBase
          onClick={handleDetail}
          sx={{
            mt: 2,
            width: '100%',
            height: 50,
            borderRadius: '14px',
            bgcolor: '#6e8df0',
            color: '#fff',
            fontSize: 16,
            fontWeight: 800,
            boxShadow: '0 4px 12px rgba(110,141,240,.35)',
            flexShrink: 0,
            '&:active': { bgcolor: '#5a78d8' },
          }}
        >
          자세히 분석
        </ButtonBase>

        {/* 하단 안내 문구 (화면 1~5 공통) */}
        <Box sx={{ flexShrink: 0 }}>
          <Disclaimer />
        </Box>
        </Box>
        {/* ↑ 스크롤 본문 끝 */}
      </Box>
    </>
  );
};

export default DigitalTwinScreen4;
